/*
  ves-pack-card-hover.js

  Shared hover-preview loader for EVERY .ves-pack-card, on any surface:
  the collection grid, product recommendations, quiz results, homepage, and
  anywhere else the ves-pack-card snippet is rendered. Single source of truth
  (previously this logic was duplicated and had drifted across three sections).

  What it does, per card:
  1. Preloads the hover clip when the card nears the viewport (Intersection
     Observer), so the first hover plays from buffer instead of downloading on
     mouseenter, kills the 2-3s blank on a fresh load.
  2. Reveals the clip only once it is ready, via `is-hover-ready` on the card.
     Until then the cover stays put on hover instead of fading to a blank gap.
     (The CSS gate lives in assets/ves-base.css.)
  3. Plays on hover, pauses on leave.

  Robustness:
  - Idempotent per card via the `data-ves-hover-bound` flag.
  - Re-scans when cards are added asynchronously (recommendations AJAX morph,
    quiz result injection, etc.) via a debounced MutationObserver, so it works
    regardless of when/how the cards reach the DOM.

  The card markup (poster + smallest-mp4 source + data-src) is produced by
  snippets/ves-pack-card.liquid.
*/

(function () {
  'use strict';
  if (window.__vesPackCardHoverInit) return;
  window.__vesPackCardHoverInit = true;

  var CARD = '.ves-pack-card';
  var HOVER = '.ves-pack-card__hover-media';
  var BOUND = 'vesHoverBound';
  // Touch devices never fire hover, so the viewport-preload below would fetch and
  // decode every card's hover clip for an interaction that can never happen. Gate
  // it on hover capability. The mouseenter handler still lazy-loads on demand, so
  // hybrid (hover-capable) devices are unaffected.
  var CAN_HOVER = !(window.matchMedia && window.matchMedia('(hover: none)').matches);

  function readyHover(card, hover) {
    if (hover.dataset.vesLoaded) return;
    hover.dataset.vesLoaded = '1';
    var src = hover.dataset.src;
    if (!src) return;
    var onReady = function () { card.classList.add('is-hover-ready'); };
    if (hover.tagName === 'VIDEO') {
      hover.addEventListener('loadeddata', onReady, { once: true });
      hover.preload = 'auto';
      hover.src = src;
      hover.load();
    } else {
      hover.addEventListener('load', onReady, { once: true });
      hover.src = src;
    }
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          var hv = entry.target.querySelector(HOVER);
          if (hv) readyHover(entry.target, hv);
        });
      }, { rootMargin: '150px 0px' })
    : null;

  function initCard(card) {
    if (card.dataset[BOUND]) return;
    var hover = card.querySelector(HOVER);
    if (!hover) return; // no hover media on this card; nothing to bind
    card.dataset[BOUND] = '1';
    // Preload near the viewport only on hover-capable devices; on touch we skip
    // the preload entirely (the clip is never shown without a hover).
    if (CAN_HOVER) { if (io) io.observe(card); else readyHover(card, hover); }
    card.addEventListener('mouseenter', function () {
      readyHover(card, hover);
      if (hover.tagName === 'VIDEO' && hover.paused) hover.play().catch(function () {});
    });
    card.addEventListener('mouseleave', function () {
      if (hover.tagName === 'VIDEO' && !hover.paused) hover.pause();
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CARD).forEach(initCard);
  }

  /* Search-modal resource cards and the bundle-contents pack carousel
     render a <video class="ves-hover-video"> when a product's hover media is
     a video. Unlike the full cards, these load ON HOVER (not viewport-preload) ,
     a search-as-you-type dropdown re-renders its results on every keystroke, so
     buffering a clip for every result would be wasteful. The poster keeps it
     from ever looking blank; resource-card's own CSS opacity crossfade reveals
     it. Delegated so it covers async-rendered search results. */
  function hoverVideo(e, play) {
    var host = e.target.closest && e.target.closest('.resource-card, .predictive-search-results__card, .ves-bundle-contents__car-card');
    if (!host) return;
    var v = host.querySelector('video.ves-hover-video, img.ves-hover-img');
    if (!v) return;
    if (play) {
      if (!v.dataset.vesLoaded && v.dataset.src) {
        v.dataset.vesLoaded = '1';
        v.src = v.dataset.src;
        if (v.tagName === 'VIDEO') v.load();
      }
      if (v.tagName === 'VIDEO' && v.paused) v.play().catch(function () {});
    } else {
      if (e.relatedTarget && host.contains(e.relatedTarget)) return;
      if (v.tagName === 'VIDEO' && !v.paused) v.pause();
    }
  }

  function init() {
    scan(document);

    document.addEventListener('mouseover', function (e) { hoverVideo(e, true); });
    document.addEventListener('mouseout', function (e) { hoverVideo(e, false); });

    if (typeof MutationObserver !== 'function') return;
    var scheduled = false;
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type !== 'childList' || m.addedNodes.length === 0) continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches(CARD)) || (n.querySelector && n.querySelector(CARD))) {
            if (!scheduled) {
              scheduled = true;
              requestAnimationFrame(function () { scheduled = false; scan(document); });
            }
            break;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
